import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import i18n from '../i18n';
import { ApiError, get, patch, post } from './api';
import { applyAppIcon, applyBrand } from './brand';
import { setDisplayTimeZone } from './format';
import type { Locale, SchoolInfo, User } from './types';

interface AuthState {
  user: User | null;
  school: SchoolInfo | null;
  ready: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setLocale: (l: Locale) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<SchoolInfo | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await get<{ user: User; school: SchoolInfo }>('/api/auth/me');
      setUser(me.user);
      setSchool(me.school);
      setDisplayTimeZone(me.school.timezone);
      applyBrand(me.school.brandColor);
      applyAppIcon(me.school.logoUrl, (me.school.defaultLocale === 'ar' && me.school.nameAr) || me.school.name);
      if (me.user.locale !== i18n.language) void i18n.changeLanguage(me.user.locale);
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) console.error(e);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => void refresh(), [refresh]);

  const logout = useCallback(async () => {
    await post('/api/auth/logout').catch(() => undefined);
    setUser(null);
  }, []);

  const setLocale = useCallback(
    async (l: Locale) => {
      await i18n.changeLanguage(l);
      if (user) {
        const r = await patch<{ user: User }>('/api/auth/me', { locale: l });
        setUser(r.user);
      }
    },
    [user],
  );

  return <Ctx.Provider value={{ user, school, ready, refresh, logout, setLocale }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
